// CORE IMPORTS
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTheme, useMediaQuery, Box, Typography } from "@mui/material";

// COMPONENTS
import { TradeSubmitButton } from "../../components/TradeSubmit";
import { BalanceDisplay } from "../../components/BalanceDisplay";
import { PriceDisplay } from "./PriceDisplay";
import { AssetSelector } from "../../components/AssetSelector";
import TargetOrderSummery from "./TargetOrderSummery";
import { TradeSummary } from "../../components/TradeSummary";
import { ClearButton } from "../../components/ClearButton";
import { TradeError } from "../../components/TradeError";
import { AlertCard, AlertStyleType } from "src/components/alerts/AlertCard";

// REDUX
import {
  OrderType,
  resetFormField,
  setOrderType,
  setTargetPrice,
  setTargetTradeAmount,
  setInitialCurrencies,
  toggleSummaryView,
  useTargetStateV2,
  resetForm,
  ITradeInput,
  setPercentageDifference,
  setBuyAssetSide,
  setSellAssetSide,
} from "src/store/features/TargetOrdersV2.slice";
import { useAppDispatch } from "src/store/store";
import { setTradeTab } from "src/store/features/trade.slice";
import { setBuyInput, setSellInput } from "src/store/features/swap.slice";
import api from "src/store/features/api";
import { useUserSlice } from "src/store/features/user.slice";

// HOOKS
import { useTargetInputV2 } from "../hooks/useTargetInputV2";
import { useTargetOrderValidationV2 } from "../hooks/useTargetOrderValidationV2";
import { use2FACheck } from "../../hooks/use2FACheck";
import usePermission from "src/utils/hooks/usePermission";

// STYLES
import { SwapStyles } from "../../swap/styles/SwapStyles";
import {
  Wrapper,
  HeaderWrapper,
  CustomInputField,
  InputsWrapper,
  InputWrapper,
  SummaryAndButtonWrapper,
  ValueText,
  MaxButton,
  StyledToggleButton,
  StyledToggleButtonGroup,
} from "../../styles/Trade.styles";

// CONSTANTS
import { HELP_TOOLTIP, STABLE_COIN_CODES } from "../../constants";

// UTILS
import { formatPrice } from "src/components/portfolio/helpers";
import { NumericFormatCustom } from "../../utils";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

export const TargetOrdersComponentV2 = () => {
  const { defaultCurrency, userHoldings, defaultTradeFee } = useUserSlice();
  const { code: userBaseCurrency } = defaultCurrency;
  const history = useHistory();

  const [formattedSellAmountValue, setFormattedSellAmountValue] = useState<string>("0");
  const [formattedBuyAmountValue, setFormattedBuyAmountValue] = useState<string>("0");
  const [placeholderCurrency, setPlaceholderCurrency] = useState<string>(userBaseCurrency);
  const [coinDataPrice, setCoinDataPrice] = useState<number | null>(null);

  const { is2FAEnabled, handle2FACheck, TwoFAAlert } = use2FACheck({
    trackingCategory: "Target_Orders",
  });
  const { isGuest } = usePermission();

  const sellInputRef = useRef<HTMLInputElement | null>(null);
  const buyInputRef = useRef<HTMLInputElement | null>(null);

  const theme = useTheme();
  const isAboveSm = useMediaQuery(theme.breakpoints.up("sm"));

  const classes = SwapStyles();
  //----------------------REDUX-----------------------------
  const { data: user } = api.useGetUserDatav2Query();
  const { orderType, sellAssetSide, buyAssetSide, viewSummary, percentageDifference } =
    useTargetStateV2();

  const [triggerTokenPrice, { data: coinData, isFetching: isCoinDataFetching }] =
    api.useLazyGetCoinPageDataQuery();

  const dispatch = useAppDispatch();
  //----------------------CONST-----------------------------
  const employee = user?.employee;
  // --------------------Hooks----------------
  const [maxExceededError, setMaxExceededError] = useState<string>("");
  const [marketPriceError, setMarketPriceError] = useState<string>("");
  const [noBaseHoldings, setNoBaseHoldings] = useState<string>("");
  const [selectedSellAssetAvailable, setSelectedSellAssetAvailable] = useState<any>(null);

  // --------------------Form Validation----------------

  /*
    - {useTargetInputV2()} validates inputs in a token input box.
    - The `useTargetInputV2` hook is used to handle input validation and update the form UI with error states, such as when inputs are zero or when the input is not valid.
    - Destructuring is used to extract specific values and methods from
    the returned object of the `useTargetInputV2` hook, including the current input value,
    its validity, and error state, whether the input has been touched, if zero input and handlers for input blur and value changes.
  */

  const {
    value: tokenUnitValue,
    isValid: tokenUnitIsValid,
    isTouched: tokenIsTouched,
    zeroInput: tokenZeroInput,
    inputBlurHandler: tokenUnitBlurHandler,
    handleTokenChange,
    handleCurrencyChange,
    handleSetMaxUnits,
  } = useTargetInputV2({
    setTargetTradeAmount,
    resetFormField,
    fieldIdentifier: "tokenValueInput",
  });

  const {
    zeroInput: currencyZeroInput,
    inputBlurHandler: currencyUnitBlurHandler,
    isValid: currencyValueValid,
    isTouched: currencyIsTouched,
  } = useTargetInputV2();

  /**
   * Validates the target order.
   * It returns an object containing the following error flags:
   * - closeToMarketError: Whether the order is within 5% of the market price.
   * - outOfRangeError: Whether the order is greater than 300% for a sell or greater than -50% for a buy order.
   * - buyTargetInvalid: Whether the target price is higher than the current market price.
   * - sellTargetInvalid: Whether the target price is lower than the current market price.
   * - insufficientBalance: Whether the user does not have sufficient funds for the target order.
   *
   *  @param {string} orderType - "Buy" or "Sell" order
   *  @param {number} [currencyValue] - the value of the currency input
   *  @param {number} [tokenValue] - the value of the token input
   *  @param {CryptoPriceRes} [coinMarketPrice] - current market price for the selected token
   *  @param {number|string} [tokenUnitValue] - the value of the token unit input
   *  @param {boolean} [tokenField] - whether the token field has been touched
   *  @param {boolean} [tokenFieldTouched] - whether the token field has been touched
   *  @param {boolean} [currencyField] - whether the currency field has been touched
   *  @param {boolean} [currencyFieldIsTouched] - whether the currency field has been touched
   *  @param {boolean} [tokenZeroInput] - whether the token input is zero
   *  @param {boolean} [currencyZeroInput] - whether the currency input is zero
   */
  const { closeToMarketError, minSpend } = useTargetOrderValidationV2(
    orderType,
    employee,
    undefined,
    undefined,
    coinData,
    tokenUnitValue,
    tokenUnitIsValid,
    tokenIsTouched,
    currencyValueValid,
    currencyIsTouched,
    tokenZeroInput,
    currencyZeroInput,
    user?.defaultTradeFee,
  );

  // --------------------HANDLERS----------------

  // displays a warning pop up if target is within 5% of current market price
  const nextHandler = () => {
    if (isGuest) {
      trackEvent(
        "Guest Tried to place a target order but was redirected to Verification",
        {},
        "Target_Orders",
      );
      history.push("/application");
      return;
    }

    if (!handle2FACheck()) return;

    if (closeToMarketError.isClose) {
      trackEvent(
        `Button_Click`,
        {
          button: "Warning_Modal_Open",
        },
        "Target_Orders",
      );
    } else {
      trackEvent(`Select_Target_Order_Next_Button`);
      dispatch(toggleSummaryView());
    }
  };

  const getCurrencyCode = (asset: ITradeInput) => {
    const currencyCode =
      asset.code !== "Select"
        ? STABLE_COIN_CODES.includes(asset.code)
          ? "USD"
          : asset.code
        : userBaseCurrency;

    return currencyCode;
  };

  useEffect(() => {
    if (buyAssetSide.code !== "Select" && sellAssetSide.code !== "Select") {
      const buyAssetCode = buyAssetSide.code !== "Select" ? buyAssetSide.code : userBaseCurrency;
      const sellAssetCode = sellAssetSide.code !== "Select" ? sellAssetSide.code : userBaseCurrency;
      const token = orderType === "Buy" ? buyAssetCode : sellAssetCode;
      const baseCurrency = orderType === "Buy" ? sellAssetCode : buyAssetCode;
      triggerTokenPrice({
        token,
        baseCurrency,
      }).then((res) => {
        if (res.data) {
          const coinData = res.data;
          setCoinDataPrice(coinData.price);
        } else {
          setCoinDataPrice(null);
        }
      });
    }
  }, [buyAssetSide.code, sellAssetSide.code, orderType]);

  useEffect(() => {
    setNoBaseHoldings("");
    if (userBaseCurrency) {
      const baseCurrencyData = userHoldings.find(
        (holding) => holding.currencyConfig.code === userBaseCurrency,
      );

      if (baseCurrencyData) {
        if (!baseCurrencyData.available && orderType === "Buy") {
          setNoBaseHoldings(
            `You do not have any ${baseCurrencyData.currencyConfig.code}. Please deposit some to place a target order.`,
          );
        }
        const userBaseCurrencyHolding: ITradeInput = {
          code: baseCurrencyData.currencyConfig.code,
          price: baseCurrencyData.price,
          units: "0",
          value: baseCurrencyData.value.toString(),
          logoURL: "",
          maxTotal: baseCurrencyData.available.toString(),
          approx: "",
        };
        dispatch(setInitialCurrencies(userBaseCurrencyHolding));
      }
    }
  }, [userBaseCurrency, orderType]);

  const handleChange = (event: any, newValue: OrderType) => {
    if (newValue !== null) {
      dispatch(setOrderType({ orderType: newValue, userBaseCurrency }));
      resetValues();
    }
  };

  const resetValues = () => {
    setFormattedSellAmountValue("");
    setFormattedBuyAmountValue("");
    setPercentageDifference(0);
    setMaxExceededError("");
    setPlaceholderCurrency(userBaseCurrency);
    setCoinDataPrice(null);
    dispatch(resetForm({ userBaseCurrency }));
  };

  const switchToSwap = (orderType: "Buy" | "Sell") => {
    if (orderType === "Sell") {
      dispatch(setSellInput(sellAssetSide));
      dispatch(setBuyInput({ ...buyAssetSide, units: "0" }));
    } else if (orderType === "Buy") {
      dispatch(setBuyInput(buyAssetSide));
      dispatch(setSellInput({ ...sellAssetSide, units: "0" }));
    }
    dispatch(setTradeTab("swap"));
  };

  useEffect(() => {
    if (orderType === "Buy" && sellAssetSide.code !== "Select") {
      const selectedHolding = userHoldings.find(
        (holding) => holding.currencyConfig.code === sellAssetSide.code,
      );
      setSelectedSellAssetAvailable(selectedHolding?.available || 0);
    } else if (orderType === "Sell" && buyAssetSide.code !== "Select") {
      // For Sell orders, update the buy side (default currency) with user's holdings
      const selectedHolding = userHoldings.find(
        (holding) => holding.currencyConfig.code === buyAssetSide.code,
      );
      if (selectedHolding) {
        const updatedBuyAssetSide: ITradeInput = {
          ...buyAssetSide,
          maxTotal: selectedHolding.available.toString(),
          price: selectedHolding.price,
        };
        dispatch(setBuyAssetSide(updatedBuyAssetSide));
      }
    }
  }, [orderType, sellAssetSide.code, buyAssetSide.code, userHoldings]);

  // Ensure holdings are populated when component mounts or holdings change
  useEffect(() => {
    if (userHoldings.length > 0 && userBaseCurrency) {
      const baseCurrencyHolding = userHoldings.find(
        (holding) => holding.currencyConfig.code === userBaseCurrency,
      );

      if (baseCurrencyHolding) {
        if (orderType === "Sell" && buyAssetSide.code === userBaseCurrency) {
          // Update buy side with user's holdings for Sell orders
          const updatedBuyAssetSide: ITradeInput = {
            ...buyAssetSide,
            maxTotal: baseCurrencyHolding.available.toString(),
            price: baseCurrencyHolding.price,
          };
          dispatch(setBuyAssetSide(updatedBuyAssetSide));
        } else if (orderType === "Buy" && sellAssetSide.code === userBaseCurrency) {
          // Update sell side with user's holdings for Buy orders
          const updatedSellAssetSide: ITradeInput = {
            ...sellAssetSide,
            maxTotal: baseCurrencyHolding.available.toString(),
            price: baseCurrencyHolding.price,
          };
          dispatch(setSellAssetSide(updatedSellAssetSide));
        }
      }
    }
  }, [userHoldings, userBaseCurrency, orderType, buyAssetSide.code, sellAssetSide.code]);

  useEffect(() => {
    const sellUnits = +sellAssetSide.units || 0;
    const maxAllowed = Number(sellAssetSide.maxTotal);
    const currencyCode = getCurrencyCode(buyAssetSide);

    let formattedInput = "";

    if (orderType === "Sell" && sellUnits && coinData?.price) {
      const sellAmount = sellUnits * coinData.price;
      formattedInput = formatPrice(sellAmount, currencyCode);

      setFormattedSellAmountValue(formattedInput);
    } else {
      setFormattedSellAmountValue("");
    }

    if (orderType === "Sell" && sellUnits > maxAllowed && maxAllowed > 0) {
      handleMaxExceeded("sell");
    } else {
      setMaxExceededError("");
    }

    if (orderType === "Buy") {
      const currencyCode = getCurrencyCode(sellAssetSide);

      if (
        sellUnits &&
        buyAssetSide.units &&
        buyAssetSide.code !== "Select" &&
        coinData?.price &&
        sellUnits > coinData.price
      ) {
        let percentageDiff = 0;
        if (sellUnits && coinData.price) {
          percentageDiff = (sellUnits / coinData.price) * 100 - 100;
        }

        setMarketPriceError(
          `Your buy target for ${buyAssetSide.code} is ${Math.abs(percentageDiff).toFixed(
            2,
          )}% above market price. Please adjust your order to be below ${coinData.price.toFixed(
            2,
          )} ${currencyCode}.`,
        );
      } else {
        setMarketPriceError("");
      }

      if (sellUnits && buyAssetSide?.price) {
        // For Buy orders, check if the user has enough of the sell currency (e.g., AUD)
        const maxAllowed = Number(sellAssetSide.maxTotal);

        if (sellUnits > maxAllowed && maxAllowed > 0) {
          handleMaxExceeded("buy");
        } else {
          setMaxExceededError("");
        }
      } else {
        setMaxExceededError("");
      }
    } else {
      setMarketPriceError("");
    }
  }, [
    sellAssetSide,
    buyAssetSide,
    orderType,
    selectedSellAssetAvailable,
    coinData,
    userBaseCurrency,
  ]);

  useEffect(() => {
    const buyUnits = +buyAssetSide.units || 0;

    if (orderType === "Buy" && buyUnits && coinData?.price) {
      const currencyCode = getCurrencyCode(sellAssetSide);

      const formattedInput = formatPrice(buyUnits * coinData.price, currencyCode);
      setFormattedBuyAmountValue(formattedInput);
    } else if (orderType === "Buy") {
      setFormattedBuyAmountValue("");
    }

    if (
      orderType === "Sell" &&
      sellAssetSide.code !== "Select" &&
      sellAssetSide.units &&
      buyUnits &&
      coinData?.price &&
      buyUnits < coinData.price
    ) {
      // Calculate percentage difference
      const percentageDiff = (buyUnits / coinData.price) * 100 - 100;
      const currencyCode = getCurrencyCode(buyAssetSide);

      setMarketPriceError(
        `Your sell target for ${sellAssetSide.code} is ${Math.abs(percentageDiff).toFixed(
          2,
        )}% below market price. Please adjust your order to be above ${Math.round(
          coinData.price,
        )} ${currencyCode}.`,
      );
    } else if (orderType === "Sell") {
      setMarketPriceError("");
    }
  }, [buyAssetSide, sellAssetSide, orderType, coinData]);

  useEffect(() => {
    if (orderType === "Sell") {
      setPlaceholderCurrency(buyAssetSide.code !== "Select" ? buyAssetSide.code : userBaseCurrency);
    } else if (orderType === "Buy") {
      setPlaceholderCurrency(
        sellAssetSide.code !== "Select" ? sellAssetSide.code : userBaseCurrency,
      );
    }
  }, [sellAssetSide, buyAssetSide]);

  useEffect(() => {
    const sellUnits = Number(sellAssetSide.units);
    const buyUnits = Number(buyAssetSide.units);

    // Need coinData for accurate market price
    if (!sellUnits || !buyUnits || !coinData?.price) {
      dispatch(setPercentageDifference(0));
      return;
    }

    const PERCENTAGE_BASE = 100;
    let percentageDiff;

    if (orderType === "Sell") {
      // For sell orders: compare buy units (target price) with market price
      percentageDiff = (buyUnits / coinData.price) * PERCENTAGE_BASE - PERCENTAGE_BASE;
    } else {
      // For buy orders: compare sell units (target price) with market price
      percentageDiff = (sellUnits / coinData.price) * PERCENTAGE_BASE - PERCENTAGE_BASE;
    }

    if (percentageDiff === 0) {
      dispatch(setPercentageDifference(0));
      return;
    }

    dispatch(setPercentageDifference(percentageDiff));
  }, [sellAssetSide.units, buyAssetSide.units, coinData, orderType]);

  useEffect(() => {
    if (sellAssetSide.code === "Select" || buyAssetSide.code === "Select") return;
    if (orderType === "Sell") {
      dispatch(setTargetPrice(+buyAssetSide.units));
    } else if (orderType === "Buy") {
      dispatch(setTargetPrice(+sellAssetSide.units));
    }
  }, [buyAssetSide.units, formattedBuyAmountValue, sellAssetSide.units, formattedSellAmountValue]);

  const handleMaxExceeded = (side: "sell" | "buy") => {
    if (side === "sell") {
      // For sell orders, check if user has enough of the token they're selling
      const maxAllowed = Number(sellAssetSide.maxTotal);
      const deficit = Number(sellAssetSide.units) - maxAllowed;

      // For sell orders, we need to show the cryptocurrency amount, not format it
      setMaxExceededError(
        `You currently have ${maxAllowed.toLocaleString()} ${
          sellAssetSide.code
        }. Please deposit additional ${deficit.toLocaleString()} ${
          sellAssetSide.code
        } or adjust your amount to place your target order.`,
      );
    } else {
      // For buy orders, check if user has enough of the currency they're spending
      const maxAllowed = Number(sellAssetSide.maxTotal);
      const deficit = Number(sellAssetSide.units) - maxAllowed;
      const currencyCode = sellAssetSide.code !== "Select" ? sellAssetSide.code : userBaseCurrency;

      // Use USD for stable coins, otherwise use the currency code (must be fiat)
      const formatCurrency = STABLE_COIN_CODES.includes(currencyCode) ? "USD" : currencyCode;

      setMaxExceededError(
        `You currently have ${formatPrice(
          maxAllowed,
          formatCurrency,
        )}. Please deposit additional ${formatPrice(
          deficit,
          formatCurrency,
        )} or adjust your amount to place your target order.`,
      );
    }
  };

  const displayAsset = orderType === "Sell" ? sellAssetSide : buyAssetSide;
  const opposingAsset = orderType === "Sell" ? buyAssetSide : sellAssetSide;

  const handleTokenChangeWithErrorClear = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMaxExceededError("");
    handleTokenChange(event);
  };

  const handleCurrencyChangeWithErrorClear = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMaxExceededError("");
    handleCurrencyChange(event);
  };

  const handleExpectedOutput = (): string => {
    if (orderType === "Sell") {
      const targetPrice = +buyAssetSide.units || 0;
      const sellUnits = +sellAssetSide.units || 0;
      const amount = sellUnits * targetPrice;
      const tradeFee = defaultTradeFee ?? 3.5;
      const fee = (tradeFee / 100) * amount;
      const total = amount - fee;
      const currencyCode = getCurrencyCode(buyAssetSide);

      return `Approx. ${formatPrice(total, currencyCode)}`;
    } else {
      return `Approx. ${buyAssetSide.units} ${buyAssetSide.code}`;
    }
  };

  const setMaxTotal = () => {
    if (orderType === "Sell" && sellSideMaxTotal) handleSetMaxUnits(sellSideMaxTotal);
  };

  const sellSideMaxTotal =
    sellAssetSide.maxTotal.length > 1 && Number(sellAssetSide.maxTotal).toString();

  const buySideMaxTotal =
    buyAssetSide.maxTotal.length > 1 && Number(buyAssetSide.maxTotal).toString();

  const minSpendErr = useMemo(() => {
    if (!minSpend) return "";

    let units: string | undefined;
    let currencyCode: string;

    if (orderType === "Sell") {
      units = buyAssetSide.units;
      currencyCode = buyAssetSide.code !== "Select" ? buyAssetSide.code : userBaseCurrency;
    } else {
      units = sellAssetSide.units;
      currencyCode = sellAssetSide.code !== "Select" ? sellAssetSide.code : userBaseCurrency;
    }

    if (units && currencyCode !== "Select") {
      return `Your target is below our minimum value of ${
        employee ? "50" : "500"
      } ${currencyCode}. Please re-enter your target.`;
    }

    return "";
  }, [
    minSpend,
    orderType,
    buyAssetSide.units,
    buyAssetSide.code,
    sellAssetSide.units,
    sellAssetSide.code,
    userBaseCurrency,
    employee,
  ]);

  const canShowSummary = useMemo(() => {
    return (
      sellAssetSide.units &&
      sellAssetSide.units !== "0" &&
      sellAssetSide.code !== "Select" &&
      buyAssetSide.units &&
      buyAssetSide.units !== "0" &&
      buyAssetSide.code !== "Select"
    );
  }, [sellAssetSide.units, sellAssetSide.code, buyAssetSide.units, buyAssetSide.code]);

  const hasErrors = !!maxExceededError || !!marketPriceError || !!minSpendErr || !!noBaseHoldings;

  const isSubmitButtonDisabled = isGuest ? false : (is2FAEnabled && !canShowSummary) || hasErrors;
  const submitButtonText = isGuest ? "Verify Now" : !is2FAEnabled ? "Enable 2FA" : "Place Target";

  if (viewSummary) return <TargetOrderSummery />;

  return (
    <Wrapper data-testid="targets-card">
      {/* Header */}
      <HeaderWrapper>
        <StyledToggleButtonGroup exclusive value={orderType} onChange={handleChange}>
          <StyledToggleButton selected={orderType === "Sell"} value="Sell">
            Sell
          </StyledToggleButton>
          <StyledToggleButton selected={orderType === "Buy"} value="Buy">
            Buy
          </StyledToggleButton>
        </StyledToggleButtonGroup>
        <ClearButton isLoading={false} text="Clear" onClick={() => resetValues()} />
      </HeaderWrapper>

      <InputsWrapper>
        {/* Input 1 */}
        <InputWrapper
          sx={{ borderRadius: "16px 16px 0 0" }}
          maxExceededError={orderType === "Sell" ? maxExceededError : ""}
          minSpendErr={orderType === "Sell" && minSpendErr ? minSpendErr : ""}>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Typography>{orderType}ing</Typography>
            <BalanceDisplay
              backgroundColor={orderType === "Sell" && maxExceededError ? "#FFF6F6" : "#F8F9F9"}
              code={
                orderType === "Sell"
                  ? sellAssetSide.code !== "Select" && sellSideMaxTotal
                    ? sellAssetSide.code
                    : ""
                  : buyAssetSide.code !== "Select" && buySideMaxTotal
                  ? buyAssetSide.code
                  : ""
              }
              balance={orderType === "Sell" ? sellSideMaxTotal || "" : buySideMaxTotal || ""}
              isPreFormatted={orderType === "Sell" ? !!sellSideMaxTotal : !!buySideMaxTotal}>
              {orderType === "Sell" && (
                <MaxButton disabled={sellAssetSide.code === "Select"} onClick={() => setMaxTotal()}>
                  Max
                </MaxButton>
              )}
            </BalanceDisplay>
          </Box>
          <Box
            width="100%"
            display="flex"
            justifyContent="flex-end"
            flexDirection="column"
            alignItems="center">
            <CustomInputField
              id="sellInput"
              inputRef={sellInputRef}
              variant="standard"
              autoComplete="off"
              value={orderType === "Sell" ? sellAssetSide.units : buyAssetSide.units}
              onChange={
                orderType === "Sell"
                  ? handleTokenChangeWithErrorClear
                  : handleCurrencyChangeWithErrorClear
              }
              onBlur={tokenUnitBlurHandler}
              placeholder={"0"}
              InputProps={{
                inputComponent: NumericFormatCustom as any,
                disableUnderline: true,
                classes: {
                  input: classes.inputStyles,
                },
                inputProps: {
                  min: 0,
                  max: 1000000,
                  maxValue: orderType === "Sell" ? Number(sellAssetSide.maxTotal) : undefined,
                },
                startAdornment: (
                  <AssetSelector
                    orderType={orderType}
                    sellAssetSide={sellAssetSide}
                    buyAssetSide={buyAssetSide}
                    side="sell"
                  />
                ),
              }}
            />
            <Box display="flex" alignSelf="flex-end" sx={{ color: "#5C6469" }}>
              <ValueText color="#5C6469" minSpendErr={orderType === "Sell" && !!minSpendErr}>
                {orderType === "Sell" ? formattedSellAmountValue : formattedBuyAmountValue}
              </ValueText>
            </Box>
          </Box>
        </InputWrapper>

        <InputWrapper
          sx={{ borderRadius: "0 0 16px 16px" }}
          marketPriceError={marketPriceError}
          maxExceededError={orderType === "Buy" ? maxExceededError : ""}
          minSpendErr={orderType === "Buy" && minSpendErr ? minSpendErr : ""}>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <ValueText>at {isAboveSm && "target price"}</ValueText>
            <PriceDisplay
              priceText={
                coinDataPrice
                  ? `1 ${displayAsset.code} = ${formatPrice(
                      coinData?.price ?? 0,
                      getCurrencyCode(opposingAsset),
                    )}`
                  : ""
              }
              isLoading={isCoinDataFetching}
              backgroundColor={orderType === "Buy" && maxExceededError ? "#FFF6F6" : "#F8F9F9"}
              buttonText={`${orderType} Market`}
              buttonDisabled={
                orderType === "Sell" ? !Number(sellAssetSide.units) : !Number(buyAssetSide.units)
              }
              onButtonClick={() => switchToSwap(orderType)}
            />
          </Box>
          <Box
            width="100%"
            display="flex"
            justifyContent="flex-end"
            flexDirection="column"
            alignItems="center">
            <CustomInputField
              id="buyInput"
              autoComplete="off"
              inputRef={buyInputRef}
              variant="standard"
              value={orderType === "Sell" ? buyAssetSide.units : sellAssetSide.units}
              onChange={
                orderType === "Sell"
                  ? handleCurrencyChangeWithErrorClear
                  : handleTokenChangeWithErrorClear
              }
              onFocus={(event) => event.target.select()}
              onBlur={currencyUnitBlurHandler}
              placeholder={formatPrice(
                0,
                STABLE_COIN_CODES.includes(placeholderCurrency) ? "USD" : placeholderCurrency,
              )}
              InputProps={{
                inputComponent: NumericFormatCustom as any,
                disableUnderline: true,
                classes: {
                  input: classes.inputStyles,
                },
                inputProps: {
                  min: 0,
                  max: 1000000,
                  maxValue: orderType === "Buy" ? Number(sellAssetSide.maxTotal) : undefined,
                  onMaxExceeded: orderType === "Buy" ? () => handleMaxExceeded("sell") : undefined,
                },
                startAdornment: (
                  <AssetSelector
                    orderType={orderType}
                    sellAssetSide={sellAssetSide}
                    buyAssetSide={buyAssetSide}
                    side="buy"
                  />
                ),
              }}
            />
            <Box display="flex" alignSelf="flex-end" sx={{ color: "#5C6469" }}>
              {percentageDifference !== 0 &&
                `(${percentageDifference > 0 ? "+" : ""}${percentageDifference.toFixed(2)}%)`}
            </Box>
          </Box>
        </InputWrapper>
      </InputsWrapper>
      {maxExceededError || marketPriceError || !!minSpendErr || !!noBaseHoldings ? (
        <TradeError
          message={maxExceededError || marketPriceError || minSpendErr || noBaseHoldings}
        />
      ) : null}

      <SummaryAndButtonWrapper>
        {canShowSummary && (
          <TradeSummary
            title={`You ${orderType}`}
            amount={orderType === "Sell" ? sellAssetSide.units : buyAssetSide.units}
            currency={orderType === "Sell" ? sellAssetSide.code : buyAssetSide.code}
            lineItems={[
              {
                label: "Target Price",
                value:
                  orderType === "Sell"
                    ? `${buyAssetSide.units} ${buyAssetSide.code}`
                    : `${sellAssetSide.units} ${sellAssetSide.code}`,
                icon: "clear",
              },
            ]}
            fee={defaultTradeFee?.toString() ?? ""}
            feeTooltip={HELP_TOOLTIP}
            expectedOutput={{
              label: "Expected Output",
              value: handleExpectedOutput(),
            }}
          />
        )}

        <TwoFAAlert />

        {isGuest && (
          <AlertCard
            titleText="Action Required"
            subText={`Please verify your account to access this feature. Verifying your account will allow you to deposit funds and place target orders. Once your account is verified, go to Settings → Security and toggle on "Enable 2 Factor Authentication" to gain full access to trading features.`}
            styleType={AlertStyleType.TRADE}
          />
        )}

        {/* Place order button */}
        <TradeSubmitButton
          text={submitButtonText}
          disabled={isSubmitButtonDisabled}
          onClick={() => nextHandler()}
        />
      </SummaryAndButtonWrapper>
    </Wrapper>
  );
};
